import { Stack, Typography } from '@mui/material';
import {
  CONDUCT_ENTRIES_GROUP_BY_FILTER_KEYS,
  CONDUCT_ENTRIES_QUERY_FILTER_KEYS,
  FilterKeys,
  FilterSection,
  GetConductEntriesQueryFilters,
  SchoolYear,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  AgeGroupExpandedSelect,
  ConductTypeExpandedSelect,
  ConductVisibilityExpandedSelect,
  ConductVisibilityTagSelect,
  DateRangeDropdown,
  FilterDropdown,
  FiltersContainer,
  FiltersSidebarMethods,
  GroupExpandedSelect,
  GroupTagSelect,
  PersonalFiltersDropdown,
  pickOnlyParamsFromFilterKeys,
  PropertyTypeExpandedSelect,
  PropertyTypeTagSelect,
  renderConductTypeOptionTags,
  renderConductTypeTags,
  renderPropertyGroupTags,
  UserExpandedSelect,
  UserTagSelect,
} from '@schooly/components/filters';
import { PROPERTIES_TEXT_IDS, SchoolPropertyType, SchoolUserRole } from '@schooly/constants';
import { CrossSmallIcon } from '@schooly/style';
import { Dispatch, FC, SetStateAction, useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';

import { ConductFiltersSidebar, GroupByOption } from './ConductFiltersSidebar';
import { useConductFilters } from './useConductFilters';

type ConductEntriesFiltersProps = {
  groupBy: GroupByOption;
  onSetGroupBy: Dispatch<SetStateAction<GroupByOption>>;
  schoolId: string;
  filters: GetConductEntriesQueryFilters;
  defaultFilters: GetConductEntriesQueryFilters;
  onSetFilters: (v: GetConductEntriesQueryFilters) => void;
  defaultSchoolYear?: SchoolYear;
  defaultUserFilters: GetConductEntriesQueryFilters;
  defaultUserGroupBy: GroupByOption;
};

export const ConductEntriesFilters: FC<ConductEntriesFiltersProps> = ({
  defaultFilters,
  onSetFilters,
  filters,
  schoolId,
  defaultSchoolYear,
  defaultUserFilters,
  onSetGroupBy,
  groupBy,
}) => {
  const sidebar = useRef<FiltersSidebarMethods | null>(null);
  const personalFiltersDropdown = useRef<PersonalFiltersDropdown | null>(null);
  const { currentStaff, permissions } = useAuth();

  const {
    labels,
    selectedItemsForAgeGroups,
    filtersDate,
    filtersGroup,
    filtersAgeGroup,
    filtersHouse,
    filtersVisibility,
    filtersStudent,
    filtersConductType,
    filtersConductSelectTypes,
    onSetDate,
    onSetAgeGroup,
    onClearAgeGroup,
    onSetHouseId,
    onClearHouse,
    onClearConductType,
    onSetConductTypesAndConductTypeOptions,
    onSelectConductStatus,
    onClearConductStatus,
    onSetGroup,
    onClearGroup,
    onSetStudentId,
    onClearStudent,
    getSchoolLevelById,
    getAgeGroupById,
    getAgeGroupsByLevelId,
  } = useConductFilters({ schoolId, filters, onSetFilters });

  const { formatMessage } = useIntl();
  const isGroupViewer = permissions.includes('group_viewer');

  const handleSaveFilter = useCallback(() => {
    personalFiltersDropdown.current?.saveFilter();
  }, []);

  const dateFilter = { [FilterKeys.Date]: filtersDate };

  return (
    <FiltersContainer onOpenFilters={() => sidebar.current?.open()}>
      <PersonalFiltersDropdown
        ref={personalFiltersDropdown}
        currentUser={currentStaff}
        relationId={currentStaff?.relation_id || ''}
        onSaveFilter={handleSaveFilter}
        schoolId={schoolId}
        section={FilterSection.Conduct}
        defaultSchoolYear={defaultSchoolYear}
        filters={{ ...filters, group_by: groupBy ?? undefined }}
        onSetFilters={(v) => {
          const appliedFilters = pickOnlyParamsFromFilterKeys(CONDUCT_ENTRIES_QUERY_FILTER_KEYS, v);
          onSetFilters({
            ...appliedFilters,
            [FilterKeys.Date]: appliedFilters.date || defaultFilters.date,
          });
          if (!v.group_by) {
            onSetGroupBy(null);
            return;
          }
          for (const key of CONDUCT_ENTRIES_GROUP_BY_FILTER_KEYS) {
            key === v.group_by && onSetGroupBy(key);
          }
        }}
      />
      {filtersDate && (
        <DateRangeDropdown
          schoolId={schoolId}
          date={filtersDate}
          onSetDate={onSetDate}
          openLabel={labels[FilterKeys.Date]}
          defaultSchoolYear={defaultSchoolYear}
        />
      )}

      {!!filtersVisibility?.length && (
        <FilterDropdown
          onClear={onClearConductStatus}
          label={labels[FilterKeys.ConductStatus]}
          tags={(open) =>
            filtersVisibility.map((v) => (
              <ConductVisibilityTagSelect
                sx={{ maxWidth: 200 }}
                key={v}
                visibility={v}
                onClick={open}
              />
            ))
          }
        >
          {(onClose) => (
            <ConductVisibilityExpandedSelect
              selectedVisibility={filtersVisibility}
              onApply={onSelectConductStatus}
              onClose={onClose}
              onClear={onClearConductStatus}
            />
          )}
        </FilterDropdown>
      )}

      {!!(filtersConductType?.length || filtersConductSelectTypes?.length) && (
        <FilterDropdown
          onClear={onClearConductType}
          label={labels[FilterKeys.ConductType]}
          tags={(open) => [
            ...renderConductTypeTags({
              ids: filtersConductType ?? [],
              onClick: open,
              tagProps: { schoolId, sx: { maxWidth: 200 } },
            }),
            ...renderConductTypeOptionTags({
              options: filtersConductSelectTypes ?? [],
              onClick: open,
              tagProps: { schoolId, sx: { maxWidth: 200 } },
            }),
          ]}
        >
          {(onClose) => (
            <ConductTypeExpandedSelect
              schoolId={schoolId}
              selectedConductTypeIds={filtersConductType ?? []}
              selectedConductTypeOptions={filtersConductSelectTypes ?? []}
              onApply={onSetConductTypesAndConductTypeOptions}
              onClose={onClose}
              onClear={onClearConductType}
            />
          )}
        </FilterDropdown>
      )}

      {!!filtersStudent?.length && (
        <FilterDropdown
          onClear={onClearStudent}
          label={labels[FilterKeys.Student]}
          tags={(open) =>
            filtersStudent.map((id) => (
              <UserTagSelect
                schoolId={schoolId}
                userType="student"
                sx={{ maxWidth: 200 }}
                key={id}
                id={id}
                onClick={open}
              />
            ))
          }
        >
          {(onClose) => (
            <UserExpandedSelect
              schoolId={schoolId}
              type="student"
              selectedIds={filtersStudent}
              onApply={onSetStudentId}
              onClose={onClose}
              onClear={onClearStudent}
              filters={dateFilter}
            />
          )}
        </FilterDropdown>
      )}

      {!!filtersAgeGroup?.length && (
        <FilterDropdown
          onClear={onClearAgeGroup}
          label={labels[FilterKeys.AgeGroup]}
          tags={(open) =>
            renderPropertyGroupTags({
              selectedItems: selectedItemsForAgeGroups,
              onClick: open,
              getProperty: (i) =>
                i.isGroup
                  ? getSchoolLevelById(i.id)
                  : { ...getAgeGroupById(i.id), type: SchoolPropertyType.AgeGroup },
              getTooltip: (i) =>
                i.isGroup
                  ? getAgeGroupsByLevelId(i.id).map((ageGroup) => (
                      <Typography key={ageGroup.id}>{ageGroup.name}</Typography>
                    ))
                  : null,
              tagProps: {
                userRole: SchoolUserRole.Student,
                sx: { maxWidth: 200 },
              },
            })
          }
        >
          {(onClose) => (
            <AgeGroupExpandedSelect
              userRole={SchoolUserRole.Student}
              schoolId={schoolId}
              selectedIds={filtersAgeGroup}
              onApply={onSetAgeGroup}
              onClose={onClose}
              onClear={onClearAgeGroup}
            />
          )}
        </FilterDropdown>
      )}

      {!!filtersHouse?.length && (
        <FilterDropdown
          onClear={onClearHouse}
          label={labels[FilterKeys.House]}
          tags={(open) =>
            filtersHouse.map((id) => (
              <PropertyTypeTagSelect
                userRole={SchoolUserRole.Student}
                sx={{ maxWidth: 200 }}
                schoolId={schoolId}
                key={id}
                id={id}
                onClick={open}
              />
            ))
          }
        >
          {(onClose) => (
            <PropertyTypeExpandedSelect
              propertyType={SchoolPropertyType.House}
              userRole={SchoolUserRole.Student}
              schoolId={schoolId}
              selectedIds={filtersHouse}
              onApply={onSetHouseId}
              onClose={onClose}
              onClear={onClearHouse}
            />
          )}
        </FilterDropdown>
      )}

      {!!filtersGroup?.length && isGroupViewer && (
        <FilterDropdown
          onClear={onClearGroup}
          label={labels[FilterKeys.Group]}
          width={500}
          tags={(open) =>
            filtersGroup.map((id) => (
              <GroupTagSelect key={id} sx={{ maxWidth: 200 }} id={id} onClick={open} />
            ))
          }
        >
          {(onClose) => (
            <GroupExpandedSelect
              selectedIds={filtersGroup}
              schoolId={schoolId}
              onApply={onSetGroup}
              onClose={onClose}
              onClear={onClearGroup}
              filters={dateFilter}
            />
          )}
        </FilterDropdown>
      )}

      {groupBy && (
        <Stack
          flexDirection="row"
          gap={0.5}
          alignItems="center"
          onClick={() => onSetGroupBy(null)}
          sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
        >
          <Typography variant="h3" color="common.grey2">
            {formatMessage({ id: 'filter-GroupBy' })}:
          </Typography>
          <Typography variant="h3">
            {formatMessage({ id: PROPERTIES_TEXT_IDS[groupBy] })}
          </Typography>
          <CrossSmallIcon color="black" />
        </Stack>
      )}

      <ConductFiltersSidebar
        ref={sidebar}
        defaultFilters={defaultFilters}
        filters={filters}
        onApply={(filters, groupBy) => {
          onSetFilters(filters);
          onSetGroupBy(groupBy);
        }}
        schoolId={schoolId}
        defaultSchoolYear={defaultSchoolYear}
        groupBy={groupBy}
      />
    </FiltersContainer>
  );
};
